import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, MonitorPlay, Rocket } from 'lucide-react';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { ArrastreContext, useArrastre } from '../hooks/useArrastre';
import { ApiError } from '../lib/api';
import {
  composicionApi,
  composicionKeys,
  type EdicionProceso,
  type EstadoProceso,
} from '../lib/composicion';
import { CatalogoHerramientas } from '../components/compositor/CatalogoHerramientas';
import { LienzoItinerario } from '../components/compositor/LienzoItinerario';
import { FantasmaArrastre } from '../components/compositor/FantasmaArrastre';
import {
  ResaltadoContext,
  ValidadorPanel,
  type Resaltado,
} from '../components/compositor/ValidadorPanel';

const ESTADO_PROCESO: Record<EstadoProceso, { texto: string; variant: BadgeVariant }> = {
  borrador: { texto: 'Borrador', variant: 'neutral' },
  activo: { texto: 'Activo', variant: 'success' },
  cerrado: { texto: 'Cerrado', variant: 'info' },
  archivado: { texto: 'Archivado', variant: 'neutral' },
};

/**
 * Compositor (/compositor/:procesoId) — superficie del FACILITADOR, densa y
 * de escritorio (tema cabina). Lienzo POR CLIC del Lote 2a: catálogo de
 * herramientas a la izquierda, itinerario fases→sesiones→actividades al
 * centro y el ValidadorPanel (Lote 3) a la derecha: clic en una advertencia
 * resalta su tarjeta/ficha destino (ring que se apaga solo a los 3 s).
 * El drag nativo (Lote 2b) es ADITIVO: baja por ArrastreContext y nunca
 * reemplaza el flujo por clic.
 */
export function CompositorPage() {
  useSurfaceTheme('cabina', 'dark');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { procesoId = '' } = useParams<{ procesoId: string }>();
  const arrastre = useArrastre();

  const arbol = useQuery({
    queryKey: composicionKeys.arbol(procesoId),
    queryFn: () => composicionApi.arbol(procesoId),
    enabled: procesoId !== '',
  });
  const herramientas = useQuery({
    queryKey: composicionKeys.herramientas,
    queryFn: composicionApi.herramientas,
  });

  // Herramienta "en mano" (selección por clic; el drag llega en el Lote 2b).
  const [seleccion, setSeleccion] = useState<string | null>(null);
  const herramientaSeleccionada = useMemo(
    () => herramientas.data?.find((h) => h.codigo === seleccion) ?? null,
    [herramientas.data, seleccion],
  );

  // Nombre editable inline (PATCH al blur), resincronizado con el servidor.
  const nombreServidor = arbol.data?.nombre ?? '';
  const [nombre, setNombre] = useState(nombreServidor);
  useEffect(() => setNombre(nombreServidor), [nombreServidor]);

  // Invalidar el árbol arrastra TAMBIÉN la validación: composicionKeys.validacion
  // anida bajo la llave del árbol (match por prefijo), así que ni esta página ni
  // las mutaciones del lienzo necesitan una segunda invalidación explícita.
  const editarProcesoMut = useMutation({
    mutationFn: (cuerpo: EdicionProceso) => composicionApi.editarProceso(procesoId, cuerpo),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: composicionKeys.arbol(procesoId) }),
  });

  // Resaltado desde el validador: guarda el destino y lo apaga solo a los 3 s.
  // Baja por ResaltadoContext hasta TarjetaSesion/FichaActividad (ring + scroll).
  const [resaltado, setResaltado] = useState<Resaltado | null>(null);
  useEffect(() => {
    if (!resaltado) return;
    const t = setTimeout(() => setResaltado(null), 3000);
    return () => clearTimeout(t);
  }, [resaltado]);

  function guardarNombre() {
    const limpio = nombre.trim();
    if (!limpio || limpio === nombreServidor) {
      setNombre(nombreServidor);
      return;
    }
    editarProcesoMut.mutate({ nombre: limpio });
  }

  const estado = arbol.data?.estado;
  const accionError = (editarProcesoMut.error as ApiError | null)?.detail ?? null;
  const cargaError = (arbol.error as ApiError | null)?.detail ?? null;

  return (
    <ArrastreContext.Provider value={arrastre}>
    <div className="flex h-screen flex-col bg-surface-base">
      {/* Barra superior: nombre editable + estado + publicar/abrir en Cabina */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-6 py-3">
        <button
          type="button"
          onClick={() => navigate(procesoId ? `/cabina?proceso=${procesoId}` : '/cabina')}
          aria-label="Volver a la Cabina"
          title="Volver a la Cabina"
          className="flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted transition-colors duration-fast hover:bg-surface-raised hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-7 w-auto" />
        <span className="hidden font-sans text-sm text-content-muted lg:inline">
          Compositor de procesos
        </span>

        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onBlur={guardarNombre}
          disabled={!arbol.data}
          aria-label="Nombre del proceso (se guarda al salir del campo)"
          title="Nombre del proceso: se guarda al salir del campo"
          className="min-h-touch min-w-0 flex-1 basis-56 rounded-md border border-transparent bg-transparent px-2 font-display text-lg font-semibold text-content transition-colors duration-fast hover:border-border focus-visible:border-border-strong focus-visible:outline-none focus-visible:shadow-focus"
        />

        {estado && <Badge variant={ESTADO_PROCESO[estado].variant}>{ESTADO_PROCESO[estado].texto}</Badge>}

        {estado === 'borrador' && (
          <Button
            variant="primary"
            size="sm"
            loading={editarProcesoMut.isPending}
            onClick={() => editarProcesoMut.mutate({ estado: 'activo' })}
            leadingIcon={<Rocket className="h-4 w-4" aria-hidden="true" />}
          >
            Publicar proceso
          </Button>
        )}
        {estado === 'activo' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(procesoId ? `/cabina?proceso=${procesoId}` : '/cabina')}
            leadingIcon={<MonitorPlay className="h-4 w-4" aria-hidden="true" />}
          >
            Abrir en Cabina
          </Button>
        )}

        {accionError && (
          <span className="flex items-center gap-1.5 font-sans text-sm text-error">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {accionError}
          </span>
        )}
      </header>

      {/* Cuerpo: catálogo | lienzo | validador (Lote 3) */}
      <div className="flex min-h-0 flex-1">
        <aside className="w-[300px] shrink-0 overflow-y-auto border-r border-border px-4 py-5">
          <CatalogoHerramientas
            herramientas={herramientas.data ?? []}
            cargando={herramientas.isLoading}
            seleccion={seleccion}
            onSeleccion={setSeleccion}
          />
        </aside>

        <ResaltadoContext.Provider value={resaltado}>
          <main className="min-w-0 flex-1 overflow-y-auto">
            {arbol.isLoading ? (
              <p className="px-6 py-5 font-sans text-sm text-content-muted">
                Cargando el lienzo del proceso…
              </p>
            ) : cargaError ? (
              <div className="max-w-lg px-6 py-5">
                <Card title="No se pudo abrir el proceso">
                  <p className="flex items-start gap-2 font-sans text-sm text-content-muted">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-error" aria-hidden="true" />
                    {cargaError}
                  </p>
                </Card>
              </div>
            ) : arbol.data ? (
              <LienzoItinerario
                arbol={arbol.data}
                procesoId={procesoId}
                herramientaSeleccionada={herramientaSeleccionada}
              />
            ) : null}
          </main>
        </ResaltadoContext.Provider>

        <aside className="flex w-[300px] shrink-0 flex-col border-l border-border">
          <ValidadorPanel procesoId={procesoId} onResaltar={setResaltado} />
        </aside>
      </div>
    </div>
      <FantasmaArrastre puntero={arrastre.puntero} />
    </ArrastreContext.Provider>
  );
}
